import moment from "moment";
import { getPosition } from "../../../utils/functions";

// How each shipment status maps onto the "Tracking Result.dc.html" design:
// a tone (purple / amber / red), the date card, the stepper and the alert.

export const TONES = {
  ok: { accent: "#7130F0", soft: "#F6F2FF", border: "#E6DCFF", tail: "rgba(113,48,240,.35)", pulse: "pulseP" },
  delay: { accent: "#B4590A", soft: "#FFF4E5", border: "#FBDDB5", tail: "rgba(180,89,10,.35)", pulse: "pulseA" },
  failed: { accent: "#C2264B", soft: "#FDECEF", border: "#F7C9D3", tail: "rgba(194,38,75,.35)", pulse: "pulseR" },
};

// Same red list the old stepper used.
const FAILED_STATUSES = [
  "RTO In-Transit",
  "RTO Out for Delivery",
  "RTO Delivered",
  "RTO Not Delivered",
  "RTO Delivery Delay",
  "Failed Delivered",
  "Failed Delivered Non-RTO",
  "Failed & Action Required",
  "Cancelled",
];
// Statuses that are never "delayed", even with a past EDD (old checkIfDelayed).
const NEVER_DELAYED = ["RTO Delivered", "RTO Out for Delivery", "RTO Initiated", "Delivered", "Cancelled", "Lost", "Out for Delivery"];
const RETURN_STATUSES = ["RTO In-Transit", "RTO Out for Delivery", "RTO Delivered", "RTO Not Delivered", "RTO Delivery Delay", "Cancelled"];
// These show the date the status was reached instead of the EDD.
const MODIFIED_DATE_STATUSES = ["Cancelled", "RTO Delivered", "Delivered", "Lost", "Damaged"];
// The failed-delivery prompt (Edit / Cancel) shows for these, as the old banner did.
export const ACTION_REQUIRED_STATUSES = ["Failed Delivered", "Failed & Action Required"];

const STEP_LABELS = {
  "Awb Generated": "Order Placed",
  "Picked-up": "Shipped",
  "In-transit": "In-Transit",
  Delivered: "Order Delivered",
  "Failed Delivered": "Delivery Failed",
  "Marked for Return": "Marked for Return",
  "Return Initiated": "Reattempt Initiated",
};
const ICON_FOR_STATUS = {
  "Awb Generated": "placed",
  "Picked-up": "shipped",
  Shipped: "shipped",
  "In-transit": "shipped",
  "Out for Delivery": "ofd",
  Delivered: "delivered",
  "Failed Delivered": "fail",
  Cancelled: "fail",
  "Marked for Return": "fail",
  "Pending with Shipper": "clock",
  "Return Initiated": "ofd",
};
const ORDINALS = { 1: "First", 2: "Second", 3: "Third", 4: "Fourth", 5: "Fifth", 6: "Sixth" };

// Tracking timestamps are IST wall-clock values stamped "+00:00".
const eventTime = (ts) => moment.utc(ts);
// Shipment dates carry their own offset (+05:30): keep that wall clock.
const shipmentDate = (value) => moment.parseZone(value);

export const formatStepDate = (ts) => eventTime(ts).format("DD MMM, YYYY . hh:mm A");

export const getTone = (status, buyerEDD) => {
  if (FAILED_STATUSES.includes(status)) return "failed";
  const pastEdd = !!buyerEDD && shipmentDate(buyerEDD).isBefore(moment().format("YYYY-MM-DD"), "day");
  if (status === "Delivery Delay" || (!NEVER_DELAYED.includes(status) && pastEdd)) return "delay";
  return "ok";
};

// finalArray: [{ status, exist, timestamp }] from shipperTrack.
export const getSteps = (finalArray, tone) => {
  const list = Array.isArray(finalArray) ? finalArray : [];
  let cur = -1;
  list.forEach((s, i) => {
    if (s?.exist) cur = i;
  });
  const steps = list.map((s, i) => {
    let icon = ICON_FOR_STATUS[s?.status] || "placed";
    if (i === cur && tone === "delay") icon = "clock";
    else if (i === cur && tone === "failed") icon = "fail";
    let note = "";
    if (i === cur && tone === "delay") note = "Delayed";
    else if (i === cur && tone === "failed" && s?.status === "Failed Delivered") note = "Attempt failed";
    else if (i === cur && s?.status === "Marked for Return") note = "Returning to seller";
    return {
      key: `${s?.status}-${i}`,
      label: STEP_LABELS[s?.status] || s?.status,
      icon,
      note,
      date: s?.timestamp ? formatStepDate(s.timestamp) : "",
    };
  });
  const finished = cur === list.length - 1 && list[cur]?.status === "Delivered";
  return { steps, cur, finished };
};

export const getDateCard = (shipment, { tone, brand }) => {
  const status = shipment?.shipmentstatus;
  if (RETURN_STATUSES.includes(status)) {
    return { label: status === "Cancelled" ? "Order Cancelled" : "Return in Progress", message: `Your order is cancelled and being returned to ${brand || "brand"}.` };
  }
  if (status === "NDR Initiated") {
    return { label: "Re-attempt Scheduled", message: "Re-attempting your order. It will be delivered soon." };
  }
  const raw = MODIFIED_DATE_STATUSES.includes(status) ? shipment?.modifieddate : shipment?.buyerEDD;
  const date = raw ? shipmentDate(raw) : null;
  const label = status === "Delivered" ? "Delivered On" : "Estimated Delivery Date";
  if (!date || !date.isValid()) return { label, message: "Date not available yet." };
  const day = date.format("DD");
  return { label, day, suffix: getPosition(day), month: date.format("MMM, YYYY"), pill: tone === "delay" ? "Running late" : "" };
};

export const getStatusText = (status, tone) => (tone === "delay" && status !== "Delivery Delay" ? "Delivery Delay" : status || "");

export const getAttemptText = (shipment) => {
  const n = shipment?.deliveryAttempts;
  if (shipment?.shipmentstatus === "Delivered" || !n || !ORDINALS[n]) return "";
  return `${ORDINALS[n]} attempt for delivery`;
};

export const getLatest = (events) => {
  const e = Array.isArray(events) ? events[0] : null;
  if (!e) return "";
  return `${e.shipmentStatus}${e.timestamp ? ` · ${eventTime(e.timestamp).format("DD MMM, YYYY, hh:mm A")}` : ""}`;
};

// modifyremark builds its text with a <strong> phone number; turn it into parts
// so it renders as React nodes, not as HTML.
export const textParts = (html) =>
  String(html || "")
    .split(/(<strong>.*?<\/strong>)/g)
    .filter(Boolean)
    .map((part) => {
      const m = part.match(/^<strong>(.*?)<\/strong>$/);
      return m ? { strong: true, text: m[1].trim() } : { strong: false, text: part.replace(/<[^>]+>/g, "").replace(/\s{2,}/g, " ").replace(/^\s+([.,])/, "$1") };
    });

// Public responses carry an obfuscated phone, not a number: show it only if it looks real.
export const displayPhone = (value) => (/^\+?\d[\d\s-]{8,}$/.test(String(value || "").trim()) ? String(value).trim() : "your registered number");

// "Delivered in 2h 47m": time from the first tracking event to the Delivered event.
export const getDeliveredIn = (events) => {
  const list = Array.isArray(events) ? events : [];
  const delivered = list.find((e) => e?.shipmentStatus === "Delivered");
  const first = list[list.length - 1];
  if (!delivered?.timestamp || !first?.timestamp || first === delivered) return "";
  const mins = eventTime(delivered.timestamp).diff(eventTime(first.timestamp), "minutes");
  if (!(mins > 0)) return "";
  const d = Math.floor(mins / 1440);
  const h = Math.floor((mins % 1440) / 60);
  const m = mins % 60;
  if (d > 0) return h ? `${d}d ${h}h` : `${d}d`;
  if (h > 0) return m ? `${h}h ${m}m` : `${h}h`;
  return `${m}m`;
};
